"use client";

import { useCallback, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { addToCart, clearCart } from "@/app/actions/cart";
import { recordAnalyticsEvent } from "@/app/actions/analytics";
import { getSessionId } from "@/lib/analytics/session";
import type {
  AddToCartInput,
  CartActionResult,
  CartItem,
} from "@/types";

/** Funnel: record add_to_cart once an item is actually added. */
function recordAddToCart(
  input: AddToCartInput,
  result: CartActionResult<CartItem>,
) {
  if (!result.success) return;
  void recordAnalyticsEvent({
    eventType: "add_to_cart",
    sessionId: getSessionId(),
    metadata: {
      product_id: input.product_id,
      partner_id: input.partner_id,
      quantity: input.quantity,
    },
  }).catch(() => {});
}

/**
 * useAddToCartGuard
 * ----------------------------------------------------------------------------
 * Wraps `addToCart` so every entry point shares one identical UX for the
 * single-partner cart rule (배민/요기요 식). When the server rejects with
 * `conflict.type === "different_partner"`, the hook opens a ResponsiveModal:
 *
 *   "장바구니에는 같은 파트너의 상품만 담을 수 있습니다.
 *    선택하신 상품을 장바구니에 담을 경우 이전에 담은 상품이 삭제됩니다."
 *
 * On confirm  → clearCart() → retry addToCart(input)
 * On cancel   → resolve with { success: false, error: 'cancelled' }
 *
 * All consumers must render `{guardDialog}` somewhere in their JSX so the
 * modal mounts. Multiple instances on the same page are fine — only the
 * active variant (mobile vs desktop) renders.
 */
export function useAddToCartGuard() {
  const t = useTranslations("cart.partnerConflict");

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    existingPartnerName: string;
    submitting: boolean;
  }>({ open: false, existingPartnerName: "", submitting: false });

  // Refs avoid stale closure across the async confirm flow.
  const pendingInputRef = useRef<AddToCartInput | null>(null);
  const resolveRef = useRef<
    ((r: CartActionResult<CartItem>) => void) | null
  >(null);

  const finishWith = useCallback((result: CartActionResult<CartItem>) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    pendingInputRef.current = null;
    setDialogState({
      open: false,
      existingPartnerName: "",
      submitting: false,
    });
  }, []);

  const addToCartWithGuard = useCallback(
    async (input: AddToCartInput): Promise<CartActionResult<CartItem>> => {
      const result = await addToCart(input);
      if (result.conflict?.type === "different_partner") {
        return new Promise<CartActionResult<CartItem>>((resolve) => {
          resolveRef.current = resolve;
          pendingInputRef.current = input;
          setDialogState({
            open: true,
            existingPartnerName: result.conflict?.existingPartnerName ?? "",
            submitting: false,
          });
        });
      }
      recordAddToCart(input, result);
      return result;
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    const input = pendingInputRef.current;
    if (!input) {
      finishWith({ success: false, error: "Invalid state" });
      return;
    }
    setDialogState((s) => ({ ...s, submitting: true }));
    const clearResult = await clearCart();
    if (!clearResult.success) {
      finishWith({
        success: false,
        error: clearResult.error || "Failed to clear cart",
      });
      return;
    }
    const retryResult = await addToCart(input);
    recordAddToCart(input, retryResult);
    finishWith(retryResult);
  }, [finishWith]);

  const handleCancel = useCallback(() => {
    finishWith({ success: false, cancelled: true });
  }, [finishWith]);

  const guardDialog = (
    <ResponsiveModal
      open={dialogState.open}
      onOpenChange={(next) => {
        if (!next && !dialogState.submitting) handleCancel();
      }}
      title={t("title")}
      description={t("description")}
      drawerHeight="fit"
      footer={
        <div className="flex w-full gap-3">
          <Button
            variant="outline"
            className="flex-1 h-12"
            onClick={handleCancel}
            disabled={dialogState.submitting}
          >
            {t("cancel")}
          </Button>
          <Button
            className="flex-1 h-12"
            onClick={handleConfirm}
            disabled={dialogState.submitting}
          >
            {t("confirm")}
          </Button>
        </div>
      }
    />
  );

  return { addToCartWithGuard, guardDialog };
}
